import { useState } from 'react';
import { supabase } from '@shared/supabase/client';

/** Outdoor-scoring maintenance RPC buttons (moved here from the retired System tab). */
export default function Maintenance() {
  return (
    <div style={{ padding: 16, borderRadius: 8, border: '1px solid rgba(15,45,100,0.4)' }}>
      <div style={{ fontSize: 'var(--fs-small)', color: '#888', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 12 }}>
        Outdoor scoring · Maintenance
      </div>
      <ActionButton
        rpc="refresh_all_board_matviews"
        label="Refresh board scores"
        description={<>Recompute <code>mv_board_environment</code>, <code>mv_board_audience_profile</code>, and <code>mv_board_scores_v2</code> in dependency order. Use after editing POI / building data (else the nightly cron at 02:45–02:55 UTC handles it).</>}
        formatResult={(d) => `env ${d.environment_ms}ms · audience ${d.audience_ms}ms · scores ${d.scores_ms}ms`}
      />
      <ActionButton
        rpc="recompute_per_source_ots"
        label="Recompute per-source OTS"
        description={<>Refill the <code>ots_osm</code> / <code>ots_2gis</code> / <code>ots_google</code> / <code>ots_composite</code> columns via <code>estimate_board_ots_v2()</code> per board × source. Powers the OTS heatmap source dropdown.</>}
        formatResult={(d) => `${d.boards_updated} boards updated`}
      />
      <ActionButton
        rpc="rebuild_board_pois_from_2gis"
        label="Rebuild 2GIS POI links"
        description={<>Re-link <code>board_pois</code> from local <code>gis_branches</code> via 500m spatial join. Use after a 2GIS import; follow with a board-score refresh.</>}
        formatResult={(d) => `${d.deleted ?? 0} deleted, ${d.inserted ?? 0} inserted`}
      />
    </div>
  );
}

interface ActionResult { total_ms?: number; elapsed_ms?: number; [key: string]: unknown }

function ActionButton({ rpc, label, description, formatResult }: { rpc: string; label: string; description: React.ReactNode; formatResult: (data: any) => string }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handleClick = async () => {
    setRunning(true); setError(null);
    const { data, error: rpcErr } = await supabase.rpc(rpc);
    setRunning(false);
    if (rpcErr) { setError(rpcErr.message ?? String(rpcErr)); return; }
    setResult(data as ActionResult);
  };
  const elapsed = result?.total_ms ?? result?.elapsed_ms ?? 0;
  return (
    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid rgba(15,45,100,0.2)' }}>
      <div style={{ fontSize: 'var(--fs-body)', color: '#bcd0ff', marginBottom: 10, lineHeight: 1.5 }}>{description}</div>
      <button onClick={handleClick} disabled={running}
        style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(74,158,255,0.5)', background: running ? 'rgba(74,158,255,0.1)' : 'rgba(74,158,255,0.2)', color: running ? '#888' : '#bcd0ff', cursor: running ? 'not-allowed' : 'pointer', fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' as never }}>
        {running ? 'Running…' : label}
      </button>
      {result && !running && (
        <div style={{ marginTop: 10, fontSize: 'var(--fs-small)', color: '#2ecc71' }}>
          Done in {(elapsed / 1000).toFixed(2)}s<span style={{ color: '#888', marginLeft: 8 }}>({formatResult(result)})</span>
        </div>
      )}
      {error && <div style={{ marginTop: 10, fontSize: 'var(--fs-small)', color: '#e74c3c' }}>{error}</div>}
    </div>
  );
}
